import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, User, Package, ArrowDown, ArrowUp, Scales, ShoppingCart, Plus, PencilSimple } from "@phosphor-icons/react";

/**
 * ProductHistoryDialog — full audit trail of a single product.
 *
 * Shows: creator info (who + when), initial stock at creation, aggregate totals
 * per movement type, and the chronological list of every event (oldest first)
 * so the user can trace how the current stock reached its value.
 */
const TYPE_META = {
  creation: { label: "Alta", color: "#7c3aed", Icon: Plus },
  entry:    { label: "Entrada", color: "#00873D", Icon: ArrowDown },
  exit:     { label: "Salida", color: "#E0000F", Icon: ArrowUp },
  adjustment: { label: "Ajuste", color: "#FFC000", Icon: Scales },
  sale:     { label: "Venta", color: "#002FA7", Icon: ShoppingCart },
  field_change: { label: "Edición", color: "#0891b2", Icon: PencilSimple },
};

export default function ProductHistoryDialog({ product, onClose }) {
  const [state, setState] = useState({ loading: false, data: null, error: null });

  useEffect(() => {
    if (!product?.id) return;
    setState({ loading: true, data: null, error: null });
    api.get(`/products/${product.id}/history`)
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((err) => setState({
        loading: false,
        data: null,
        error: err?.response?.data?.detail || "No se pudo cargar el historial",
      }));
  }, [product?.id]);

  if (!product) return null;

  const d = state.data;
  const createdAt = d?.created_at ? new Date(d.created_at) : null;
  const daysAgo = createdAt ? Math.max(0, Math.floor((new Date() - createdAt) / (24 * 3600 * 1000))) : null;

  return (
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock size={20} weight="duotone" className="text-[#002FA7]" />
            Historial de {product.name}
          </DialogTitle>
        </DialogHeader>

        {state.loading && <div className="py-10 text-center text-zinc-400">Cargando…</div>}
        {state.error && <div className="py-10 text-center text-red-500">{state.error}</div>}

        {d && (
          <div className="space-y-5">
            {/* Provenance panel */}
            <section className="bg-zinc-50 border border-zinc-200 rounded-md p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard
                label="Dado de alta por"
                value={d.created_by_name || d.created_by_email || "—"}
                sub={createdAt ? `${createdAt.toLocaleDateString("es-MX")} · hace ${daysAgo} día${daysAgo === 1 ? "" : "s"}` : ""}
                icon={<User size={14} weight="bold" />}
              />
              <StatCard
                label="Stock inicial"
                value={d.initial_stock}
                sub="cantidad ingresada al crear"
                icon={<Package size={14} weight="bold" />}
                emphasize
              />
              <StatCard
                label="Stock actual"
                value={d.current_stock}
                sub={d.current_stock === d.initial_stock ? "sin movimientos posteriores" : `${d.current_stock - d.initial_stock >= 0 ? "+" : ""}${d.current_stock - d.initial_stock} vs inicial`}
                icon={<Package size={14} weight="bold" />}
                accent={d.current_stock <= 0 ? "danger" : d.current_stock < 3 ? "warn" : "ok"}
              />
            </section>

            {/* Totals per type */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Totales acumulados</div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {Object.entries(TYPE_META).map(([k, m]) => {
                  const val = d.totals?.[k] || 0;
                  const Icon = m.Icon;
                  const suffix = k === "field_change" ? "" : ""; // qty for stock types, count for edits
                  return (
                    <div
                      key={k}
                      className={`p-2 rounded-md border text-center ${val > 0 ? "bg-white border-zinc-200" : "bg-zinc-50 border-zinc-100 opacity-50"}`}
                    >
                      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold" style={{ color: m.color }}>
                        <Icon size={11} weight="bold" /> {m.label}
                      </div>
                      <div className="font-heading font-extrabold text-lg mt-1 numeric" style={{ color: val > 0 ? m.color : "#a1a1aa" }}>
                        {val}{suffix}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Timeline */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
                Línea de tiempo ({d.timeline?.length ?? d.movements?.length ?? 0} eventos)
              </div>
              <div className="border border-zinc-200 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-100 text-[10px] uppercase text-zinc-500">
                    <tr>
                      <th className="text-left px-3 py-2">Fecha</th>
                      <th className="text-left px-3 py-2">Tipo</th>
                      <th className="text-left px-3 py-2">Detalle</th>
                      <th className="text-right px-3 py-2">Stock</th>
                      <th className="text-left px-3 py-2">Usuario</th>
                    </tr>
                  </thead>
                  <tbody data-testid="history-timeline">
                    {((d.timeline || d.movements) || []).length === 0 ? (
                      <tr><td colSpan={5} className="px-3 py-8 text-center text-zinc-400">Sin eventos registrados</td></tr>
                    ) : (d.timeline || d.movements || []).map((ev) => {
                      const meta = TYPE_META[ev.type] || { label: ev.type, color: "#52525b", Icon: Scales };
                      const Icon = meta.Icon;
                      const isFieldChange = ev.type === "field_change";
                      return (
                        <tr key={ev.id} className="border-t border-zinc-100 hover:bg-zinc-50 align-top">
                          <td className="px-3 py-2 numeric text-zinc-600 whitespace-nowrap">
                            {new Date(ev.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm" style={{ color: meta.color, background: meta.color + "15" }}>
                              <Icon size={10} weight="bold" /> {meta.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-zinc-700 max-w-[380px]">
                            {isFieldChange ? (
                              <FieldChangeDetail ev={ev} />
                            ) : (
                              <StockDetail ev={ev} />
                            )}
                          </td>
                          <td className="px-3 py-2 text-right numeric text-zinc-600 whitespace-nowrap">
                            {isFieldChange ? "—" : (ev.stock_after ?? "—")}
                          </td>
                          <td className="px-3 py-2 text-zinc-600 truncate max-w-[140px]" title={ev.user_email}>
                            {ev.user_name || ev.user_email || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, sub, icon, emphasize, accent }) {
  const accentClass = accent === "danger"
    ? "text-[#E0000F]"
    : accent === "warn"
      ? "text-amber-600"
      : "text-zinc-900";
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold inline-flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`mt-1 font-heading font-extrabold ${emphasize ? "text-2xl text-[#002FA7]" : `text-xl ${accentClass}`} numeric truncate`} title={String(value)}>
        {typeof value === "number" ? value.toLocaleString("es-MX") : value}
      </div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const MONEY_FIELDS = new Set(["cost", "min_price", "reseller_price", "marketplace_price", "store_price"]);

function fmtVal(field, v) {
  if (v === null || v === undefined || v === "") return "—";
  if (MONEY_FIELDS.has(field)) {
    const n = Number(v);
    if (!isNaN(n)) return `MX$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return String(v);
}

function FieldChangeDetail({ ev }) {
  return (
    <div className="leading-snug">
      <span className="font-semibold text-zinc-900">{ev.field_label || ev.field}</span>
      <div className="text-[11px] mt-0.5 inline-flex flex-wrap items-center gap-1.5">
        <span className="line-through text-zinc-400">{fmtVal(ev.field, ev.old_value)}</span>
        <span className="text-zinc-400">→</span>
        <span className="font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded px-1.5 py-0.5">
          {fmtVal(ev.field, ev.new_value)}
        </span>
      </div>
    </div>
  );
}

function StockDetail({ ev }) {
  const sign = ev.type === "entry" || ev.type === "creation" ? "+" : ev.type === "adjustment" ? "" : "−";
  const meta = TYPE_META[ev.type];
  const color = meta?.color || "#52525b";
  return (
    <div className="leading-snug">
      <span className="font-semibold numeric" style={{ color }}>
        {sign}{Math.abs(ev.quantity || 0)} pz
      </span>
      {ev.note && (
        <div className="text-[11px] text-zinc-500 mt-0.5 truncate max-w-[340px]" title={ev.note}>
          {ev.note}
        </div>
      )}
    </div>
  );
}
